import type { Adv } from "@/types/adv"
import type { BiometryCurves } from "@/types/biometry"
import type { APIError, CountryInfo } from "@/types/common"
import type { AvailableExam, AvailableMarker } from "@/types/exam"
import type { CrlMeasurement, WeekPercentiles } from "@/types/pregnancy"
import { acceptHMRUpdate, defineStore } from "pinia"
import Api from "@/api"

export const useStaticStore = defineStore("static", {
	state: () => ({
		// Adv
		adv: [] as Adv[],
		loadingAdv: false,

		// Countries
		countries: [] as CountryInfo[],
		loadingCountries: false,

		// Exams
		exams: [] as AvailableExam[],
		loadingExams: false,

		// Exams - Markers
		markers: [] as AvailableMarker[],
		loadingMarkers: false,

		// Pregnancy - Fetal Weight
		fetalWeight: [] as WeekPercentiles[],
		loadingFetalWeight: false,

		// Pregnancy - Mother Weight Gain
		motherWeightGain: [] as WeekPercentiles[],
		loadingMotherWeightGain: false,

		// Pregnancy - Gestational Age CRL
		gestationalAgeCRL: [] as CrlMeasurement[],
		loadingGestationalAgeCRL: false,

		// Biometry - Curves
		biometryCurves: {} as BiometryCurves,
		loadingBiometryCurves: false,

		// Registration - Unfair Clauses
		unfairClauses: null as string | null,
		loadingUnfairClauses: false
	}),
	actions: {
		// Adv Actions
		async getAdv(): Promise<Adv[]> {
			if (this.adv.length || this.loadingAdv) {
				return this.adv
			}

			this.loadingAdv = true

			try {
				const response = await Api.static.adv.list()
				const data = response.data || []

				this.adv = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingAdv = false
			}
		},

		// Countries Actions
		async getCountries(): Promise<CountryInfo[]> {
			if (this.countries.length || this.loadingCountries) {
				return this.countries
			}

			this.loadingCountries = true

			try {
				const response = await Api.static.countries.list()
				const data = response.data || []

				this.countries = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingCountries = false
			}
		},

		// Exams Actions
		async getExams(): Promise<AvailableExam[]> {
			if (this.exams.length || this.loadingExams) {
				return this.exams
			}

			this.loadingExams = true

			try {
				const response = await Api.static.exams.list()
				const data = response.data || []

				this.exams = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingExams = false
			}
		},

		// Exams - Markers Actions
		async getMarkers(): Promise<AvailableMarker[]> {
			if (this.markers.length || this.loadingMarkers) {
				return this.markers
			}

			this.loadingMarkers = true

			try {
				const response = await Api.static.exams.markers()
				const data = response.data || []

				this.markers = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingMarkers = false
			}
		},

		// Pregnancy - Fetal Weight Actions
		async getFetalWeight(): Promise<WeekPercentiles[]> {
			if (this.fetalWeight.length || this.loadingFetalWeight) {
				return this.fetalWeight
			}

			this.loadingFetalWeight = true

			try {
				const response = await Api.static.pregnancy.fetalWeight()
				const data = response.data || []

				this.fetalWeight = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingFetalWeight = false
			}
		},

		// Pregnancy - Mother Weight Gain Actions
		async getMotherWeightGain(): Promise<WeekPercentiles[]> {
			if (this.motherWeightGain.length || this.loadingMotherWeightGain) {
				return this.motherWeightGain
			}

			this.loadingMotherWeightGain = true

			try {
				const response = await Api.static.pregnancy.motherWeightGain()
				const data = response.data || []

				this.motherWeightGain = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingMotherWeightGain = false
			}
		},

		// Pregnancy - Gestational Age CRL Actions
		async getGestationalAgeCRL(): Promise<CrlMeasurement[]> {
			if (this.gestationalAgeCRL.length || this.loadingGestationalAgeCRL) {
				return this.gestationalAgeCRL
			}

			this.loadingGestationalAgeCRL = true

			try {
				const response = await Api.static.pregnancy.gestationalAgeCRL()
				const data = response.data || []

				this.gestationalAgeCRL = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingGestationalAgeCRL = false
			}
		},

		// Biometry - Curves Actions
		async getBiometryCurves(): Promise<BiometryCurves> {
			if (Object.keys(this.biometryCurves).length > 0 || this.loadingBiometryCurves) {
				return this.biometryCurves
			}

			this.loadingBiometryCurves = true

			try {
				const response = await Api.static.biometry.curves()
				const data = response.data || {}

				this.biometryCurves = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingBiometryCurves = false
			}
		},

		// Registration - Unfair Clauses Actions
		async getUnfairClauses(): Promise<string | null> {
			if (this.unfairClauses || this.loadingUnfairClauses) {
				return this.unfairClauses
			}

			this.loadingUnfairClauses = true

			try {
				const response = await Api.static.registration.unfairClauses()
				const data = response.data?.text || null

				this.unfairClauses = data
				return data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingUnfairClauses = false
			}
		},

		// Group Actions
		async loadExamsData(): Promise<void> {
			await Promise.all([this.getExams(), this.getMarkers()])
		},

		async loadPregnancyData(): Promise<void> {
			await Promise.all([this.getFetalWeight(), this.getMotherWeightGain(), this.getGestationalAgeCRL()])
		},

		// Load All Action
		async loadAll(options?: { logged?: boolean }): Promise<void> {
			const proms: Promise<Adv[] | CountryInfo[] | string | null | void | BiometryCurves>[] = [
				this.getAdv(),
				this.getCountries(),
				this.getUnfairClauses()
			]

			if (options?.logged) {
				proms.push(this.loadExamsData(), this.loadPregnancyData(), this.getBiometryCurves())
			}

			await Promise.all(proms)
		}
	},
	persist: {
		pick: [
			"countries",
			"exams",
			"markers",
			"fetalWeight",
			"motherWeightGain",
			"gestationalAgeCRL",
			"biometryCurves"
		]
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useStaticStore, import.meta.hot))
}
